import { Props_SnippetAccordion } from "./Accordion.props";
import SnippetLink from '../Shared/Link/Link';
import { useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { CompileMarkdown } from '../../../../Shared/AttraxTs/React/ReactSnippetMDCompiler'

const ANIMATION_DURATION = 0.3;

type AccordionRowProps = {
    accordionRow: Props_SnippetAccordion['repeatable_accordionRow'][number];
    index: number;
    isOpen: boolean;
    isDetailsOpen: boolean;
    onToggle: (index: number, event: React.MouseEvent) => void;
    onExitComplete: (index: number) => void;
};

const AccordionRow = ({ accordionRow, index, isOpen, isDetailsOpen, onToggle, onExitComplete }: AccordionRowProps) => {
    const { linkText, linkHref, linkStyle, linkTarget, title, text } = accordionRow;

    return (
        <details
            key={"accordion-row-" + index}
            className={`snippet-accordion__row ${isOpen ? "snippet-accordion__row--open" : ""}`}
            open={isDetailsOpen}
        >
            <summary
                className="snippet-accordion__row-title"
                onClick={(e) => onToggle(index, e)}
            >
                {title?.value ? title.value : "Accordion Row " + (index + 1)}
            </summary>
            <AnimatePresence initial={false} onExitComplete={() => onExitComplete(index)}>
                {isOpen && (
                    <motion.div
                        className="snippet-accordion__content-container"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: ANIMATION_DURATION, ease: "easeInOut" }}
                    >
                        {text?.value && (
                            <div className="snippet-accordion__text">{CompileMarkdown(text.value)}</div>
                        )}
                        {linkHref?.value && linkText?.value && (
                            <SnippetLink
                                linkStyles={linkStyle?.value}
                                className="snippet-accordion__link"
                                url={linkHref?.value}
                                openInNewTab={!!linkTarget?.value}
                                rel={linkTarget ? "noopener noreferrer" : undefined}
                            >
                                {linkText?.value}
                            </SnippetLink>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </details>
    );
};

const Accordion = (props: Props_SnippetAccordion) => {
    const { accordionTitle, accordionBackground, paragraph, cta, repeatable_accordionRow } = props;
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const [detailsOpenSet, setDetailsOpenSet] = useState<Set<number>>(new Set());
    const titleText = accordionTitle?.text?.value ?? "";
    const paragraphText = paragraph?.text?.value ?? "";
    const titleVisible = accordionTitle.visibility?.value !== false;
    const hasVisibleTitle = titleVisible && !!titleText;
    const hasParagraph = !!paragraphText.trim();
    const hasCta = !!cta?.linkHref?.value?.trim() && !!cta?.linkText?.value?.trim();
    const isFullWidthLayout = !titleVisible && !hasParagraph && !hasCta;
    const hasSupportContent = hasParagraph || hasCta;
    const backgroundTheme = accordionBackground?.color?.value;
    const backgroundColorStyle = backgroundTheme ? `snippet-accordion--${backgroundTheme}` : "";

    const handleToggle = (index: number, event: React.MouseEvent) => {
        event.preventDefault();
        
        if (openIndex === index) {
            setOpenIndex(null);
        } else {
            setOpenIndex(index);
            setDetailsOpenSet(prev => new Set(prev).add(index));
        }
    };

    const handleExitComplete = (index: number) => {
        setDetailsOpenSet(prev => {
            const newSet = new Set(prev);
            newSet.delete(index);
            return newSet;
        });
    };

    return (
        <section className={`snippet-accordion ${backgroundColorStyle} ${isFullWidthLayout ? "snippet-accordion--full-width" : ""}`}>
            <div className="snippet-accordion__container">
                {!titleVisible && !!titleText && (
                    <div className="snippet-accordion__title accessible-heading">
                        {CompileMarkdown(titleText)}
                    </div>
                )}

                {hasVisibleTitle && (
                    <div className="snippet-accordion__title-block">
                        <div className="snippet-accordion__title">
                            {CompileMarkdown(titleText)}
                        </div>
                    </div>
                )}

                <div className="snippet-accordion__row-container">
                    {repeatable_accordionRow.map((accordionRow, index) => (
                        <AccordionRow
                            key={"accordion-row-" + index}
                            accordionRow={accordionRow}
                            index={index}
                            isOpen={openIndex === index}
                            isDetailsOpen={detailsOpenSet.has(index)}
                            onToggle={handleToggle}
                            onExitComplete={handleExitComplete}
                        />
                    ))}
                </div>

                {hasSupportContent && (
                    <div className="snippet-accordion__support">
                        {hasParagraph && (
                            <div className="snippet-accordion__paragraph">
                                {CompileMarkdown(paragraphText)}
                            </div>
                        )}

                        {hasCta && (
                            <SnippetLink
                                linkStyles={cta?.linkStyle?.value}
                                className="snippet-accordion__cta-link"
                                url={cta?.linkHref?.value}
                                openInNewTab={!!cta?.linkTarget?.value}
                                rel={cta?.linkTarget?.value ? "noopener noreferrer" : undefined}
                            >
                                {cta?.linkText?.value}
                            </SnippetLink>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
};

export default Accordion;
